<template>
  <div>
    <BFormGroup :label="$t('homeBanners.image')">
      <div class="d-flex align-items-center">
        <img
          v-if="form.icon"
          :src="imageSrc"
          class="banner-image-preview mr-3"
        >
        <label class="btn btn-sm btn-outline-secondary mb-0">
          <i class="far fa-image mr-1" />
          {{ form.icon ? $t('general.change') : $t('homeBanners.selectImage') }}
          <input
            type="file"
            accept="image/*"
            class="d-none"
            @change="onImageSelected"
          >
        </label>
        <button
          v-if="form.icon"
          class="btn btn-sm btn-outline-danger ml-2"
          @click="removeImage"
        >
          <i class="fas fa-times mr-1" />
          {{ $t('general.remove') }}
        </button>
      </div>
      <div
        v-if="imageError"
        class="text-danger small mt-1"
      >
        {{ imageError }}
      </div>
      <div class="text-muted small mt-1">
        {{ $t('homeBanners.imageHint') }}
      </div>
    </BFormGroup>

    <BFormGroup :label="$t('homeBanners.backgroundColor')">
      <div class="d-flex align-items-center flex-wrap">
        <button
          v-for="color in suggestedColors"
          :key="color"
          class="banner-color-swatch mr-2"
          :class="{ selected: selectedColor === color }"
          :style="{ backgroundColor: color }"
          :title="$t('homeBanners.suggestedColor')"
          @click="form.backgroundColor = color"
        />
        <label
          class="banner-color-swatch banner-color-picker mb-0"
          :class="{ selected: hasCustomColor }"
          :style="hasCustomColor ? { background: selectedColor } : null"
          :title="$t('homeBanners.customColor')"
        >
          <input
            :value="selectedColor || '#ffffff'"
            type="color"
            @input="form.backgroundColor = $event.target.value"
          >
        </label>
      </div>
      <div
        v-if="suggestedColors.length"
        class="text-muted small mt-1"
      >
        {{ $t('homeBanners.suggestedColorsHint') }}
      </div>
    </BFormGroup>

    <BFormGroup :label="$t('homeBanners.title')">
      <BFormInput
        v-model="form.title"
        :maxlength="titleMaxLength"
        :state="form.title.trim() ? null : false"
      />
      <div class="text-muted small text-right">
        {{ form.title.length }}/{{ titleMaxLength }}
      </div>
    </BFormGroup>

    <BFormGroup :label="$t('homeBanners.description')">
      <BFormTextarea
        v-model="form.description"
        :maxlength="descriptionMaxLength"
        rows="3"
      />
      <div class="text-muted small text-right">
        {{ form.description.length }}/{{ descriptionMaxLength }}
      </div>
    </BFormGroup>

    <BFormGroup :label="$t('homeBanners.url')">
      <BFormInput
        v-model="form.url"
        type="url"
        placeholder="https://"
        :state="urlValid ? null : false"
      />
      <div class="text-muted small mt-1">
        {{ $t('homeBanners.urlHint') }}
      </div>
    </BFormGroup>

    <BFormGroup
      v-if="form.url.trim()"
      :label="$t('homeBanners.buttonText')"
    >
      <BFormInput
        v-model="form.buttonText"
        :maxlength="buttonTextMaxLength"
        :state="form.buttonText.trim() ? null : false"
      />
    </BFormGroup>

    <div class="row">
      <div class="col-6">
        <BFormGroup :label="$t('homeBanners.dateFrom')">
          <CtkDateTimePicker
            v-model="form.dateFrom"
            locale="pl"
            format="YYYY-MM-DD HH:mm"
            formatted="YYYY-MM-DD HH:mm"
            :label="$t('homeBanners.noLimit')"
          />
        </BFormGroup>
      </div>
      <div class="col-6">
        <BFormGroup :label="$t('homeBanners.dateTo')">
          <CtkDateTimePicker
            v-model="form.dateTo"
            locale="pl"
            format="YYYY-MM-DD HH:mm"
            formatted="YYYY-MM-DD HH:mm"
            :label="$t('homeBanners.noLimit')"
          />
        </BFormGroup>
      </div>
    </div>
    <div
      v-if="!datesValid"
      class="text-danger small mb-2"
    >
      {{ $t('homeBanners.invalidDates') }}
    </div>

    <BFormGroup :label="$t('homeBanners.groups')">
      <div class="text-muted small mb-2">
        {{ $t('homeBanners.groupsHint') }}
      </div>
      <GroupSelect
        :group-codes.sync="form.groupCodes"
        show-select-all
      />
    </BFormGroup>

    <SaveButton
      class="mt-3"
      :disabled="!valid"
      @save="save"
      @cancel="$emit('cancel')"
    />
  </div>
</template>

<script>
import GroupSelect from '@/components/payers/form/GroupSelect';
import SaveButton from '@/components/SaveButton';
import moment from 'moment';
import CtkDateTimePicker from 'vue-ctk-date-time-picker';

const DATE_FORMAT = 'YYYY-MM-DD HH:mm';
const IMAGE_MAX_SIZE = 800;
// Banners are stored base64-encoded in a payee property and downloaded by every app user
const IMAGE_MAX_BYTES = 200 * 1024;

const SUGGESTED_COLORS_COUNT = 5;
// Minimum RGB distance between two suggestions, so they aren't near-duplicates
const SUGGESTED_COLORS_MIN_DISTANCE = 48;

const toHex = (rgb) => `#${rgb.map((x) => x.toString(16).padStart(2, '0')).join('')}`;
const distance = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);

// Most common colors of the image, most common first. Pixels are bucketed into
// 16 levels per channel and each bucket is averaged, so close shades merge.
// Colors along the left edge come first - in the app the image sits on the right
// of the banner, so its left edge is what touches the background.
const extractColors = (image) => {
  const scale = Math.min(1, 64 / Math.max(image.width, image.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.width * scale));
  canvas.height = Math.max(1, Math.round(image.height * scale));
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const edgeWidth = Math.max(1, Math.round(canvas.width * 0.1));

  const buckets = new Map();
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] >= 128) {
      const key = [data[i], data[i + 1], data[i + 2]].map((c) => Math.floor(c / 16)).join(',');
      const bucket = buckets.get(key) || {
        r: 0, g: 0, b: 0, count: 0, edge: 0,
      };
      bucket.r += data[i];
      bucket.g += data[i + 1];
      bucket.b += data[i + 2];
      bucket.count += 1;
      if ((i / 4) % canvas.width < edgeWidth) bucket.edge += 1;
      buckets.set(key, bucket);
    }
  }

  const ranked = [...buckets.values()]
    .map((x) => ({
      rgb: [x.r, x.g, x.b].map((c) => Math.round(c / x.count)),
      count: x.count,
      edge: x.edge,
    }))
    .sort((a, b) => b.count - a.count);
  const edgeColor = [...ranked].sort((a, b) => b.edge - a.edge)[0];

  const picked = [];
  [edgeColor, ...ranked].forEach((x) => {
    if (x && picked.length < SUGGESTED_COLORS_COUNT
      && picked.every((y) => distance(x.rgb, y) >= SUGGESTED_COLORS_MIN_DISTANCE)) {
      picked.push(x.rgb);
    }
  });

  return picked.map(toHex);
};

const toLocal = (iso) => (iso ? moment(iso).format(DATE_FORMAT) : null);
const toIso = (local) => (local ? moment(local, DATE_FORMAT).toISOString() : null);

const loadImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = e.target.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// Scales the image down and re-encodes it as JPEG, lowering the quality until it fits the limit
const compressImage = (image) => {
  const scale = Math.min(1, IMAGE_MAX_SIZE / Math.max(image.width, image.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(image.width * scale);
  canvas.height = Math.round(image.height * scale);

  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  for (let quality = 0.85; quality >= 0.4; quality -= 0.15) {
    const base64 = canvas.toDataURL('image/jpeg', quality).split(',')[1];
    if (base64.length <= IMAGE_MAX_BYTES) {
      return base64;
    }
  }

  return null;
};

export default {
  props: {
    banner: Object,
  },
  data: () => ({
    titleMaxLength: 60,
    descriptionMaxLength: 150,
    buttonTextMaxLength: 20,
    imageError: null,
    suggestedColors: [],
    form: {
      icon: '',
      title: '',
      description: '',
      url: '',
      buttonText: '',
      backgroundColor: '',
      dateFrom: null,
      dateTo: null,
      groupCodes: [],
    },
  }),
  components: {
    CtkDateTimePicker,
    GroupSelect,
    SaveButton,
  },
  computed: {
    imageSrc() {
      return this.form.icon.startsWith('data:')
        ? this.form.icon
        : `data:image/jpeg;base64,${this.form.icon}`;
    },
    datesValid() {
      return !this.form.dateFrom
        || !this.form.dateTo
        || moment(this.form.dateFrom, DATE_FORMAT).isBefore(moment(this.form.dateTo, DATE_FORMAT));
    },
    selectedColor() {
      return this.form.backgroundColor.toLowerCase();
    },
    hasCustomColor() {
      return !!this.selectedColor && !this.suggestedColors.includes(this.selectedColor);
    },
    urlValid() {
      const url = this.form.url.trim();
      return !url || /^https?:\/\/\S+$/i.test(url);
    },
    buttonValid() {
      return !this.form.url.trim() || !!this.form.buttonText.trim();
    },
    valid() {
      return !!this.form.title.trim() && this.datesValid && this.urlValid && this.buttonValid;
    },
  },
  methods: {
    async onImageSelected(e) {
      const [file] = e.target.files;
      e.target.value = '';
      if (!file) return;

      this.imageError = null;
      try {
        const image = await loadImage(file);
        const icon = compressImage(image);
        if (icon) {
          this.form.icon = icon;
          this.suggestedColors = extractColors(image);
          this.form.backgroundColor = this.suggestedColors[0] || '';
        } else {
          this.imageError = this.$t('homeBanners.imageTooLarge');
        }
      } catch {
        this.imageError = this.$t('homeBanners.imageInvalid');
      }
    },
    removeImage() {
      this.form.icon = '';
      this.suggestedColors = [];
    },
    async suggestColorsFromIcon() {
      try {
        const image = new Image();
        await new Promise((resolve, reject) => {
          image.onload = resolve;
          image.onerror = reject;
          image.src = this.imageSrc;
        });
        this.suggestedColors = extractColors(image);
      } catch {
        this.suggestedColors = [];
      }
    },
    save() {
      const groupCodes = this.form.groupCodes.filter((x) => x !== '#all');
      const url = this.form.url.trim();

      this.$emit('save', {
        ...(this.banner || {}),
        icon: this.form.icon || undefined,
        title: this.form.title.trim(),
        description: this.form.description.trim() || undefined,
        url: url || undefined,
        buttonText: url ? this.form.buttonText.trim() : undefined,
        backgroundColor: this.form.backgroundColor || undefined,
        dateFrom: toIso(this.form.dateFrom) || undefined,
        dateTo: toIso(this.form.dateTo) || undefined,
        groupCodes: groupCodes.length > 0 ? groupCodes : undefined,
      });
    },
  },
  created() {
    if (!this.banner) return;

    this.form = {
      icon: this.banner.icon || '',
      title: this.banner.title || '',
      description: this.banner.description || '',
      url: this.banner.url || '',
      buttonText: this.banner.buttonText || '',
      backgroundColor: this.banner.backgroundColor || '',
      dateFrom: toLocal(this.banner.dateFrom),
      dateTo: toLocal(this.banner.dateTo),
      groupCodes: this.banner.groupCodes || [],
    };

    if (this.form.icon) {
      this.suggestColorsFromIcon();
    }
  },
};
</script>

<style lang="scss" scoped>
.banner-color-input {
  width: 48px;
  height: 32px;
  padding: 2px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  cursor: pointer;
}

.banner-color-swatch {
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 50%;
  cursor: pointer;

  &.selected {
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px #333;
  }
}

.banner-color-picker {
  position: relative;
  overflow: hidden;
  background: conic-gradient(red, yellow, lime, aqua, blue, magenta, red);

  input {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
  }
}

.banner-image-preview {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
}
</style>
